import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { cn } from "@weco/ui";
import { getValidAccessTokenFromStorage, STORAGE_USER, WECO_USER_UPDATED_EVENT } from "@weco/suite-client";
import type { AppConfig } from "./apps.config";
import { SuiteSidebar, type SuiteChromeMode, type SidebarRoute, type SidebarGroup, type SidebarLabels } from "./SuiteSidebar";
import { BottomNav, MobileNavMenu, type BottomNavItem, type MobileNavLabels } from "./MobileNav";
import { useShellViewport } from "./useShellViewport";
import { SuiteTopbar, type TopbarLabels } from "./SuiteTopbar";
import type { SuiteChromeUser } from "./types";
import { SuiteChromeProvider } from "./SuiteChromeContext";
import { SuiteAppsBootstrap } from "./SuiteAppsContext";
import { initSuiteThemeFromStorage } from "./initTheme";
import { initSuiteLocaleFromStorage } from "./initLocale";
import { readUserFromStorage } from "./readUser";
import { applyTenantBrandingToDOM, fetchTenantBranding } from "./tenantBranding";
import { ChatDrawer } from "./chat/ChatDrawer";
import { ChatMiniBar } from "./chat/ChatMiniBar";
import "./suite-chrome.css";

export interface SuiteAppShellProps {
  children: React.ReactNode;
  mode: SuiteChromeMode;
  currentAppId?: string;
  shellUser?: SuiteChromeUser | null;
  onShellSignOut?: () => void | Promise<void>;
  /** Click handler for the logo. Convention: navigate to the default landing. */
  onLogoClick?: () => void;
  /** Avatar menu "View profile". */
  onProfileClick?: () => void;
  /** When set, sidebar/topbar app list is loaded from People `/api/v1.0/config/apps/`. */
  loadSuiteApps?: () => Promise<AppConfig[]>;
  /** Bumps dynamic app list reload when changed (e.g. user id). */
  suiteAppsReloadKey?: string;
  /**
   * Predicate that, when returning true for the current pathname AND the viewport
   * is mobile (<768px), collapses the full shell chrome (sidebar/topbar/bottom
   * nav) so the embedded app can own the entire viewport.
   */
  collapseForMobileRoute?: (pathname: string) => boolean;
  /** Tenant branding logo URL — replaces the default "w" mark. */
  brandingLogoUrl?: string;
  /** Tenant branding app name — shown next to the mark and used as alt text. */
  brandingAppName?: string;
  /** Grouped sidebar navigation (already filtered to what the user may open). */
  sidebarGroups?: ReadonlyArray<SidebarGroup>;
  /** Secondary section routes (All apps, Settings). */
  sidebarSecondary?: ReadonlyArray<SidebarRoute>;
  /** Route id to highlight in the sidebar / bottom nav / apps sheet. */
  activeRouteId?: string | null;
  /** Header tab rail (desktop/tablet). */
  headerTabs?: React.ReactNode;
  /** Header work status; receives whether the condensed (mobile) form is wanted. */
  renderHeaderStatus?: (opts: { compact: boolean }) => React.ReactNode;
  /** Mobile bottom navigation items (already filtered). */
  bottomNavItems?: ReadonlyArray<BottomNavItem>;
  /** Hide the bottom nav for this pathname (focused editors, Call). */
  hideBottomNav?: (pathname: string) => boolean;
  /** Route id to mark with the pulsing "incoming call" dot (sidebar, mobile menu, bottom nav). */
  ringingRouteId?: string | null;
  /** Unread counts shown on sidebar rows, by route id (e.g. the Tasks Inbox). */
  sidebarBadges?: Readonly<Record<string, number>>;
  /** Translated search placeholder — defaults to English. */
  searchPlaceholder?: string;
  /** Translated topbar labels. */
  topbarLabels?: TopbarLabels;
  /** Translated sidebar labels. */
  sidebarLabels?: SidebarLabels;
  /** Translated mobile nav labels. */
  mobileNavLabels?: MobileNavLabels;
}

/**
 * The suite frame (design: `weco2/shell.jsx` App).
 *
 * A tinted outer frame with a 2px gutter holds two `workspace-strip` panels:
 * the sidebar (16px top-start radius) and the main column (16px top-end
 * radius). The header sits on the main column's strip; the content panel
 * below it is `bg-background`, so the active header tab flares straight into
 * it. `suite-shell` / `suite-shell-main` stay as styling hooks for the glass
 * layer.
 */
export const SuiteAppShell: React.FC<SuiteAppShellProps> = ({
  children,
  mode,
  currentAppId,
  shellUser,
  onShellSignOut,
  onLogoClick,
  onProfileClick,
  loadSuiteApps,
  suiteAppsReloadKey,
  collapseForMobileRoute,
  brandingLogoUrl,
  brandingAppName,
  sidebarGroups,
  sidebarSecondary,
  activeRouteId,
  headerTabs,
  renderHeaderStatus,
  bottomNavItems,
  hideBottomNav,
  ringingRouteId = null,
  sidebarBadges,
  searchPlaceholder,
  topbarLabels,
  sidebarLabels,
  mobileNavLabels,
}) => {
  const [initialTheme] = useState(() => initSuiteThemeFromStorage());
  const [initialLocale] = useState(() => initSuiteLocaleFromStorage());
  const [userSnap, setUserSnap] = useState<SuiteChromeUser | null>(() => readUserFromStorage());
  const viewport = useShellViewport();
  const isMobile = viewport === "mobile";
  const isTablet = viewport === "tablet";
  // Starts collapsed so the module gets the most workspace. Crossing below
  // desktop closes it (it now overlays); otherwise the user's toggle stands.
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isAppsOpen, setIsAppsOpen] = useState(false);
  const location = useLocation();
  const predicateResult = collapseForMobileRoute ? collapseForMobileRoute(location.pathname) === true : false;
  const collapseChrome = isMobile && predicateResult;
  const showBottomNav =
    isMobile && !collapseChrome && (bottomNavItems?.length ?? 0) > 0 && !(hideBottomNav?.(location.pathname) ?? false);

  useEffect(() => {
    if (viewport !== "desktop") setIsSidebarOpen(false);
    if (viewport !== "mobile") setIsAppsOpen(false);
  }, [viewport]);

  // Navigating anywhere closes the overlays.
  useEffect(() => {
    setIsAppsOpen(false);
    if (viewport === "tablet") setIsSidebarOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Toasts and floating buttons clear the bottom nav (see the design's
  // `html.has-bottom-nav` rules).
  useEffect(() => {
    document.documentElement.classList.toggle("has-bottom-nav", showBottomNav);
    return () => document.documentElement.classList.remove("has-bottom-nav");
  }, [showBottomNav]);

  useEffect(() => {
    const sync = () => setUserSnap(readUserFromStorage());
    window.addEventListener("focus", sync);
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_USER) sync();
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener(WECO_USER_UPDATED_EVENT, sync);
    return () => {
      window.removeEventListener("focus", sync);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(WECO_USER_UPDATED_EVENT, sync);
    };
  }, []);

  useEffect(() => {
    if (mode !== "remote") return;
    let cancelled = false;
    void (async () => {
      const branding = await fetchTenantBranding(getValidAccessTokenFromStorage);
      if (!cancelled) applyTenantBrandingToDOM(branding);
    })();
    return () => {
      cancelled = true;
    };
  }, [mode]);

  const user = shellUser ?? userSnap;

  if (collapseChrome) {
    return (
      <SuiteChromeProvider initialTheme={initialTheme} initialLocale={initialLocale}>
        <SuiteAppsBootstrap loadApps={loadSuiteApps} reloadKey={suiteAppsReloadKey}>
          <div
            data-suite-shell="collapsed"
            className="h-screen overflow-hidden flex flex-col bg-background text-foreground"
          >
            <main className="flex-1 min-h-0 overflow-hidden relative flex flex-col">{children}</main>
            <ChatMiniBar />
          </div>
        </SuiteAppsBootstrap>
      </SuiteChromeProvider>
    );
  }

  const groups = sidebarGroups ?? [];
  const secondary = sidebarSecondary ?? [];

  return (
    <SuiteChromeProvider initialTheme={initialTheme} initialLocale={initialLocale}>
      <SuiteAppsBootstrap loadApps={loadSuiteApps} reloadKey={suiteAppsReloadKey}>
        <div
          // Classes, not inline style, so the glass layer (glass-theme.css)
          // can still turn the 2px gutter into its own.
          className={cn(
            "suite-shell flex h-dvh text-foreground overflow-hidden font-sans antialiased bg-foreground/12",
            isMobile ? "p-0" : "px-[2px] pt-[2px] pb-0",
          )}
        >
          {!isMobile && (
            <SuiteSidebar
              mode={mode}
              currentAppId={currentAppId}
              user={user}
              brandingLogoUrl={brandingLogoUrl}
              brandingAppName={brandingAppName}
              groups={groups}
              secondary={secondary}
              activeRouteId={activeRouteId}
              isOpen={isSidebarOpen}
              isTablet={isTablet}
              onToggle={() => setIsSidebarOpen((v) => !v)}
              onClose={() => setIsSidebarOpen(false)}
              onLogoClick={onLogoClick}
              sidebarLabels={sidebarLabels}
              ringingRouteId={ringingRouteId}
              badges={sidebarBadges}
            />
          )}
          <main
            className="suite-shell-main workspace-strip flex-1 overflow-hidden flex flex-col min-h-0 min-w-0 relative"
            style={{ borderStartEndRadius: isMobile ? 0 : 16 }}
          >
            <SuiteTopbar
              mode={mode}
              currentAppId={currentAppId}
              user={user}
              onShellSignOut={onShellSignOut}
              onLogoClick={onLogoClick}
              onProfileClick={onProfileClick}
              brandingAppName={brandingAppName}
              brandingLogoUrl={brandingLogoUrl}
              searchPlaceholder={searchPlaceholder}
              topbarLabels={topbarLabels}
              condensed={isMobile}
              onMenuToggle={() => setIsAppsOpen((v) => !v)}
              isMenuOpen={isAppsOpen}
              tabs={isMobile ? undefined : headerTabs}
              status={renderHeaderStatus?.({ compact: isMobile })}
            />
            {/* Content panel: one continuous surface flush beneath the tab
                strip. The active tab flares straight into it. */}
            <div className="flex-1 min-h-0 min-w-0 overflow-hidden flex flex-col relative w-full bg-background">
              {children}
            </div>
            {showBottomNav && (
              <BottomNav
                items={bottomNavItems ?? []}
                activeRouteId={activeRouteId ?? null}
                moreOpen={isAppsOpen}
                onMore={() => setIsAppsOpen((v) => !v)}
                labels={mobileNavLabels}
                ringingRouteId={ringingRouteId}
              />
            )}
          </main>
          {isMobile && (
            <MobileNavMenu
              isOpen={isAppsOpen}
              onClose={() => setIsAppsOpen(false)}
              groups={groups}
              secondary={secondary}
              activeRouteId={activeRouteId ?? null}
              user={user}
              brandingAppName={brandingAppName}
              brandingLogoUrl={brandingLogoUrl}
              ringingRouteId={ringingRouteId}
              labels={{
                searchPlaceholder,
                searchAriaLabel: topbarLabels?.searchAriaLabel,
                notificationsLabel: topbarLabels?.notificationsLabel,
                notificationsEmptyLabel: topbarLabels?.notificationsEmptyLabel,
                notifications: topbarLabels?.notifications,
                closeMenuLabel: topbarLabels?.closeMenuLabel,
                ...mobileNavLabels,
              }}
            />
          )}
          <ChatDrawer />
          <ChatMiniBar />
        </div>
      </SuiteAppsBootstrap>
    </SuiteChromeProvider>
  );
};
